import type { useClerk, useSignIn, useSignUp } from "@clerk/clerk-react";
import { ApiError } from "../../../lib/http";
import { ROUTES } from "../../../routes/paths";

/**
 * Clerk-backed auth, driven entirely from our own screens.
 *
 * These wrap Clerk's headless "custom flow" API so no Clerk-hosted UI is ever
 * shown: the pages own the markup and call in here with the resource object
 * from `useSignIn()` / `useSignUp()`. Hooks cannot live in a module like this,
 * so the resource is passed in rather than read here.
 *
 * The Clerk instance must be configured to match: Email address as the
 * identifier, "Email verification code" on, and Password off. With password
 * still required, verification succeeds but no session is created - the
 * `missingFields` detail in the errors below names whatever is still missing.
 */

/** The in-flight attempt objects Clerk hands back from its hooks. */
type SignInResource = NonNullable<ReturnType<typeof useSignIn>["signIn"]>;
type SignUpResource = NonNullable<ReturnType<typeof useSignUp>["signUp"]>;
/** The Clerk instance itself, from `useClerk()`. */
type ClerkInstance = ReturnType<typeof useClerk>;
/** Derived from the resource rather than imported, like the two above. */
type SignUpCreateParams = Parameters<SignUpResource["create"]>[0];

/** Shape of a Clerk API rejection. Matched structurally so the screens keep
 *  their `instanceof ApiError` branch for server messages and their generic
 *  branch for network failures. */
type ClerkApiError = {
  status?: number;
  errors?: Array<{
    code?: string;
    message?: string;
    longMessage?: string;
    /** Names the field an error is about - which param was unknown, say. */
    meta?: { paramName?: string };
  }>;
};

/** Reads the codes off either a raw Clerk rejection or one already re-thrown
 *  as an `ApiError`, which carries them in `data`. */
const clerkErrors = (error: unknown): ClerkApiError["errors"] => {
  if (error instanceof ApiError) {
    return Array.isArray(error.data)
      ? (error.data as ClerkApiError["errors"])
      : undefined;
  }
  const candidate = error as ClerkApiError | null;
  return Array.isArray(candidate?.errors) ? candidate?.errors : undefined;
};

/** Re-throws Clerk API rejections as `ApiError` and leaves everything else
 *  (network, programmer error) alone for the screens' generic message. */
function rethrow(error: unknown): never {
  const errors = clerkErrors(error);
  const first = errors?.[0];
  if (first) {
    throw new ApiError(
      first.longMessage || first.message || "Something went wrong.",
      (error as ClerkApiError).status ?? 0,
      // Carried through so a caller can still branch on the code. The screens
      // show `message`, but "that address is already registered" is the one
      // rejection that needs a different answer from all the others, and by
      // then the original rejection is gone.
      errors
    );
  }
  throw error;
}

/**
 * The parameters Clerk rejected as ones it does not collect - `firstName` on an
 * instance with name collection turned off, `legalAccepted` on one without
 * legal consent enabled. Named in the API's own casing (`first_name`).
 */
const unknownParams = (error: unknown): string[] =>
  clerkErrors(error)
    ?.filter((e) => e.code === "form_param_unknown")
    .map((e) => e.meta?.paramName)
    .filter((name): name is string => !!name) ?? [];

/** `first_name` and `firstName` are the same parameter to us. */
const sameParam = (a: string, b: string) =>
  a.replace(/_/g, "").toLowerCase() === b.replace(/_/g, "").toLowerCase();

/**
 * True when Clerk refused a sign-up because that address already has an
 * account. Worth telling apart from every other failure: it is the one the
 * user cannot fix by trying again, only by logging in instead.
 */
export const isEmailTakenError = (error: unknown) =>
  clerkErrors(error)?.some((e) => e.code === "form_identifier_exists") ?? false;

/** True when the Google identity that came back belongs to no account here. */
export const isUnknownGoogleAccountError = (error: unknown) =>
  clerkErrors(error)?.some((e) => e.code === "external_account_not_found") ??
  false;

// ---------------------------------------------------------------------------
// Sign up
// ---------------------------------------------------------------------------

/**
 * The create parameters an instance may simply not collect, and which it
 * rejects outright rather than ignoring when it does not.
 *
 * Which ones are wanted is a setting in the Clerk dashboard - name collection
 * on or off, legal consent on or off - so rather than the app encoding a guess
 * at that configuration, each of these is offered and dropped again if Clerk
 * says it does not want it. `emailAddress` and `unsafeMetadata` are not in
 * here: without them there is no sign-up worth completing.
 */
const OPTIONAL_SIGNUP_PARAMS = [
  "firstName",
  "lastName",
  "legalAccepted",
] as const;

/**
 * `signUp.create`, retried without whatever optional parameters this instance
 * turns out not to collect.
 *
 * Clerk names the offending parameter on the rejection, so each pass drops
 * exactly what it complained about. The set above is finite and every pass
 * removes at least one member of it or rethrows, so this ends.
 */
async function createSignUp(
  signUp: SignUpResource,
  params: SignUpCreateParams
): Promise<void> {
  let next: SignUpCreateParams = { ...params };

  for (;;) {
    const attempt = next;
    try {
      await signUp.create(attempt);
      return;
    } catch (error) {
      const rejected = unknownParams(error);
      const droppable = OPTIONAL_SIGNUP_PARAMS.filter(
        (name) => name in attempt && rejected.some((it) => sameParam(it, name))
      );
      // Nothing of ours to give up - a taken address, a bad email, an instance
      // that wants something we never sent. The caller's problem, not ours.
      if (droppable.length === 0) throw error;

      next = Object.fromEntries(
        Object.entries(attempt).filter(
          ([key]) => !droppable.some((name) => name === key)
        )
      );
    }
  }
}

/**
 * Starts a sign-up and sends the email code.
 *
 * `legalAccepted` is what the consent screen's ticks amount to as far as Clerk
 * is concerned: with legal consent enabled on the instance, a sign-up without
 * it verifies the code and then refuses to complete, naming `legal_accepted` as
 * still required. It is true here because this is only ever reached from the
 * consent screen, and only once every box on it is ticked.
 *
 * The name is written to both the profile fields and `unsafeMetadata`, so that
 * an instance which does not collect names still keeps what the user entered.
 */
export async function signup(
  signUp: SignUpResource,
  fullName: string,
  email: string
): Promise<void> {
  const [firstName, ...rest] = fullName.split(/\s+/);
  const lastName = rest.join(" ");

  try {
    await createSignUp(signUp, {
      emailAddress: email,
      firstName,
      lastName,
      legalAccepted: true,
      unsafeMetadata: { fullName },
    });

    await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
  } catch (error) {
    rethrow(error);
  }
}

/** Verifies the sign-up code. Returns the id of the session to activate. */
export async function verifySignupOtp(
  signUp: SignUpResource,
  otp: string
): Promise<string> {
  try {
    const attempt = await signUp.attemptEmailAddressVerification({ code: otp });

    if (attempt.status !== "complete" || !attempt.createdSessionId) {
      throw new ApiError(incompleteMessage(attempt.missingFields), 400);
    }
    return attempt.createdSessionId;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    rethrow(error);
  }
}

export async function resendSignupOtp(signUp: SignUpResource): Promise<void> {
  try {
    await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
  } catch (error) {
    rethrow(error);
  }
}

// ---------------------------------------------------------------------------
// Log in
// ---------------------------------------------------------------------------

/**
 * Starts a sign-in and sends the email code. Returns the id of the email
 * address the code went to, which `resendLoginOtp` needs to send another.
 */
export async function login(
  signIn: SignInResource,
  email: string
): Promise<{ emailAddressId: string }> {
  try {
    const attempt = await signIn.create({ identifier: email });

    const factor = attempt.supportedFirstFactors?.find(
      (f): f is typeof f & { emailAddressId: string } =>
        f.strategy === "email_code"
    );
    if (!factor) {
      throw new ApiError(
        "Email code sign-in is not enabled for this account.",
        400
      );
    }

    await signIn.prepareFirstFactor({
      strategy: "email_code",
      emailAddressId: factor.emailAddressId,
    });

    return { emailAddressId: factor.emailAddressId };
  } catch (error) {
    if (error instanceof ApiError) throw error;
    rethrow(error);
  }
}

/** Verifies the log-in code. Returns the id of the session to activate. */
export async function verifyLoginOtp(
  signIn: SignInResource,
  otp: string
): Promise<string> {
  try {
    const attempt = await signIn.attemptFirstFactor({
      strategy: "email_code",
      code: otp,
    });

    if (attempt.status !== "complete" || !attempt.createdSessionId) {
      throw new ApiError(
        "This account needs another step to finish signing in. Please contact support.",
        400
      );
    }
    return attempt.createdSessionId;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    rethrow(error);
  }
}

export async function resendLoginOtp(
  signIn: SignInResource,
  emailAddressId: string
): Promise<void> {
  try {
    await signIn.prepareFirstFactor({ strategy: "email_code", emailAddressId });
  } catch (error) {
    rethrow(error);
  }
}

// ---------------------------------------------------------------------------
// Google
// ---------------------------------------------------------------------------

/**
 * Which screen started a Google handshake, and so which resource it went out
 * through: a log-in starts a sign-in, a sign-up starts a sign-up. The callback
 * screen reads it back to decide how to answer a handshake that ends without a
 * session.
 */
export type OAuthIntent = "login" | "signup";

const OAUTH_INTENT_PARAM = "intent";
const OAUTH_INTENT_KEY = "naurist.oauthIntent";

/**
 * The intent is remembered twice on purpose. The query parameter is what the
 * callback reads and it survives the trip in the URL; the session copy is there
 * for a Clerk redirect that does not carry the parameter back. Neither outlives
 * the browser session, and the callback clears the copy as it reads it.
 */
function rememberIntent(intent: OAuthIntent): void {
  try {
    sessionStorage.setItem(OAUTH_INTENT_KEY, intent);
  } catch {
    // Storage can be denied outright. The query parameter still carries it.
  }
}

/** Where Clerk sends the browser back to, carrying the intent with it. */
const callbackUrl = (intent: OAuthIntent) =>
  `${ROUTES.ssoCallback}?${OAUTH_INTENT_PARAM}=${intent}`;

/**
 * Sends the browser straight to Google's consent screen - no Clerk screen in
 * between. Clerk returns to `ROUTES.ssoCallback`, which finishes the handshake
 * and forwards to `redirectUrlComplete`.
 *
 * A log-in creates nothing, so it goes out through `signIn` and asserts no
 * agreement to anything.
 */
export async function startGoogleLogin(
  signIn: SignInResource,
  redirectUrlComplete: string
): Promise<void> {
  rememberIntent("login");

  try {
    await signIn.authenticateWithRedirect({
      strategy: "oauth_google",
      redirectUrl: callbackUrl("login"),
      redirectUrlComplete,
    });
  } catch (error) {
    rethrow(error);
  }
}

/**
 * The same trip out, started as a sign-up.
 *
 * Through `signUp`, not `signIn`, and that is the whole point of it being a
 * function of its own. Started as a sign-in, an address Clerk has never seen
 * becomes an account only by *transfer* on the way back - a `signUp.create`
 * that runs inside `handleRedirectCallback`, on the callback screen, from code
 * that is not ours. Everything that step needs has to be in place on a screen
 * that exists only to redirect: the bot check it mounts, the agreement it
 * carries, the fields the instance asks for. When any of it is not, Clerk
 * answers by sending the user to `signUpUrl` - back to the sign-up form, with
 * no session and nothing said.
 *
 * Started here, the account is created by this call instead, on the consent
 * screen: the one place that already carries the bot-check element, that knows
 * the name the form collected, and that can put a failure in front of the user
 * as an error rather than a redirect. The callback is then left with a sign-up
 * to finish rather than one to invent.
 *
 * `legalAccepted` still travels with it. On an instance with legal consent
 * enabled a sign-up without it verifies and then refuses to complete, and this
 * is only ever reached from the consent screen with every box ticked.
 */
export async function startGoogleSignup(
  signUp: SignUpResource,
  redirectUrlComplete: string,
  fullName?: string
): Promise<void> {
  rememberIntent("signup");

  try {
    await signUp.authenticateWithRedirect({
      strategy: "oauth_google",
      redirectUrl: callbackUrl("signup"),
      redirectUrlComplete,
      legalAccepted: true,
      // Google supplies the name too, but only this side knows what the user
      // typed. Kept for the same reason the email route keeps it: an instance
      // that does not collect names still has it here.
      ...(fullName ? { unsafeMetadata: { fullName } } : {}),
    });
  } catch (error) {
    rethrow(error);
  }
}

/**
 * Reads the intent back on the callback screen and clears the copy it kept.
 *
 * Anything it cannot recognise counts as a log-in, which is the reading that
 * blocks the transfer: a sign-up that stops with a message is recoverable,
 * while an account created without consent is not.
 */
export function takeGoogleOAuthIntent(search: string): OAuthIntent {
  const fromUrl = new URLSearchParams(search).get(OAUTH_INTENT_PARAM);

  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(OAUTH_INTENT_KEY);
    sessionStorage.removeItem(OAUTH_INTENT_KEY);
  } catch {
    // Same as above - the query parameter is the primary carrier anyway.
  }

  return (fromUrl ?? stored) === "signup" ? "signup" : "login";
}

/**
 * True when the handshake that just came back found no account for that Google
 * identity. With `transferable: false` Clerk leaves the sign-in attempt sitting
 * in this state rather than creating one, and does not always reject - so the
 * callback screen has to ask.
 */
export function isUnknownGoogleAccount(clerk: ClerkInstance): boolean {
  const verification = clerk.client?.signIn?.firstFactorVerification;
  return (
    verification?.status === "transferable" ||
    verification?.error?.code === "external_account_not_found"
  );
}

/** Names what the Clerk instance still wants, so a mis-configured instance
 *  (password left on, say) says so instead of failing blankly. */
function incompleteMessage(missingFields: readonly string[] | undefined) {
  const missing = missingFields?.length ? missingFields.join(", ") : "";
  return missing
    ? `Your account could not be completed - still required: ${missing}.`
    : "Your account could not be completed. Please try again.";
}
